import type {
  MultipleAvailableOpportunity,
  OpportunityStatus,
  SingleAvailableOpportunity
} from '@ps/isp-web-services-types';
import type { PlaceBetResultRunner } from '@ps/web-sports-types';
import { isEqual } from 'lodash';

import type { Entity } from '../entities';
import {
  sportsCompetition,
  sportsEvent,
  sportsMarket,
  sportsRace,
  sportsSelection,
  sportsSport
} from '../entities';

import { getIsOddsBoostMarket } from './utils/getIsOddsBoostMarket';
import { getRunnerStatus } from './utils/getRunnerStatus';

export const createMultipleEntities =
  (opportunities: SingleAvailableOpportunity[]) =>
  (bettableOpportunities: MultipleAvailableOpportunity[], resultRunners?: PlaceBetResultRunner[]) =>
    bettableOpportunities.flatMap((multiple) =>
      multiple.runnersIds.flatMap((runnerId) => {
        const opportunity = opportunities.find((opportunity) =>
          isEqual(opportunity.runnerId, runnerId)
        );
        const resultRunner =
          resultRunners &&
          resultRunners.find((resultRunner) => isEqual(resultRunner.runner, runnerId));

        if (!opportunity) {
          return [];
        }

        const selectionOdds = resultRunner ? resultRunner.winOdds : opportunity.winRunnerOdds;

        const trackedSelection = sportsSelection({
          selectionId: runnerId.selectionId,
          selectionOdds: selectionOdds?.decimalDisplayOdds?.decimalOdds,
          selectionStatus: getRunnerStatus(opportunity.status as OpportunityStatus),
          isStartingPrice: !selectionOdds
        });
        const trackedEvent = sportsEvent({
          eventId: Number(opportunity.eventId),
          eventStartTime: new Date(opportunity.eventStartTime).getTime()
        });
        const trackedMarket = sportsMarket({
          marketId: runnerId.marketId,
          marketInPlay: false,
          marketIsOddsBoost: getIsOddsBoostMarket(opportunity.marketType)
        });
        const sportEntity = sportsSport({ sportId: Number(opportunity.eventTypeId) });

        const entities: Entity[] = [trackedSelection, trackedEvent, trackedMarket, sportEntity];

        if (opportunity.competitionId) {
          const competitionEntity = sportsCompetition({ competitionId: opportunity.competitionId });
          entities.push(competitionEntity);
        }

        if (opportunity.raceId) {
          const raceIdEntity = sportsRace({ raceId: opportunity.raceId });
          entities.push(raceIdEntity);
        }

        return entities;
      })
    );
